import { Alert, Button, Card, Space } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    Image as ZhImage
} from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { YES_OR_NO } from "@/meta/common";
import { useUserStore } from "@/stores/react/authStore";
import { ADVERT_TYPE, BUTTON_TYPE, CLASSIFY } from "@/views/pages/manageCenter/adPageManage";
import type { AdPageManagementRow } from "./AdPageManagementPage";

export interface AdPageDetailPageProps {
    id?: number;
    loadDetail?: (id: number) => Promise<AdPageManagementRow>;
    loadPicture?: (id: number) => Promise<{ path?: string }>;
    loadButtonValue?: (buttonType?: number | string, buttonValue?: string) => Promise<string | undefined>;
    resolveContent?: (content: string) => Promise<string>;
    permissions?: string[];
}

/** 广告按钮关联值加载器，保持旧 Vue 详情页按按钮类型回查关联实体的契约。 */
export type AdPageButtonValueLoader = (
    buttonType?: number | string,
    buttonValue?: string
) => Promise<string | undefined>;

/** 复用 Vue 广告详情接口。 */
export async function loadAdPageDetail(id: number): Promise<AdPageManagementRow> {
    const response = await request.service.get<AdPageManagementRow>(`/dataService/admin/advert/find/${id}`);
    return response.data;
}

/** 复用 Vue 文件服务获取广告缩略图地址。 */
export async function loadAdPagePicture(id: number): Promise<{ path?: string }> {
    const response = await request.service.get<{ path?: string }>(`/dataService/admin/file/${id}`);
    return response.data;
}

/**
 * 复用 Vue setButtonValue 的四类关联查询，直接调用兼容 API，避免把 Vue service 装饰器带入 React 构建。
 */
export async function loadAdPageButtonValue(
    buttonType?: number | string,
    buttonValue?: string
): Promise<string | undefined> {
    if (!buttonValue) return undefined;
    const value = encodeURIComponent(buttonValue);

    switch (Number(buttonType)) {
        case BUTTON_TYPE.PRODUCT.code: {
            const response = await request.service.get<{
                productConsignment?: {
                    title?: string;
                    productTypeName?: string;
                    productTypeSpec?: string;
                    id?: number | string;
                };
            }>(`/admin/product-consignment/vo/${value}`);
            const product = response.data.productConsignment;
            if (!product) return undefined;
            return [
                product.title,
                product.productTypeName ? `${product.productTypeName}(${product.productTypeSpec || ""})` : undefined,
                product.id
            ]
                .filter(item => item !== undefined && item !== "")
                .join(" | ");
        }
        case BUTTON_TYPE.PRODUCT_TYPE.code: {
            const response = await request.service.get<{ name?: string }>(`/dataService/admin/product-type/${value}`);
            return response.data.name;
        }
        case BUTTON_TYPE.WHARF.code: {
            const response = await request.service.get<{ source?: string; destination?: string }>(
                `/dataService/admin/ship/route/${value}`
            );
            return [response.data.source, response.data.destination].filter(Boolean).join(" - ");
        }
        case BUTTON_TYPE.INFORMATION.code: {
            const response = await request.service.get<{ information?: { title?: string } }>(
                `/dataService/admin/information/${value}`
            );
            return response.data.information?.title;
        }
        default:
            return undefined;
    }
}

/** 复用 Vue 富文本管理器替换内容中的媒体地址。 */
export async function resolveAdPageContent(content: string): Promise<string> {
    const rawContent = content.replace(/^==ids=(.*?)\n/, "");
    const mediaPattern = /<(img|video|source)\s+[^>]*src=["']([^"']+)["']/g;
    const mediaSources = Array.from(rawContent.matchAll(mediaPattern)).map(match => match[2]);
    const mediaIds = Array.from(
        new Set(mediaSources.map(source => Number(source.match(/#(\d+)$/)?.[1])).filter(Boolean))
    );
    if (!mediaIds.length) return rawContent;
    const response = await request.service.post<Array<{ id: number; path: string }>>(
        "/dataService/admin/file/ids",
        mediaIds
    );
    let resolved = rawContent;
    mediaSources.forEach(source => {
        const id = Number(source.match(/#(\d+)$/)?.[1]);
        const file = response.data.find(item => item.id === id);
        if (file) resolved = resolved.replaceAll(source, `${file.path}#${file.id}`);
    });
    return resolved;
}

function formatDate(value?: string): string {
    return value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE;
}

const RICH_TEXT_CONTENT_STYLE = { height: "400px", overflowY: "hidden" } as const;

const formatMeta = (
    meta: { getIntroByCode: (code: number | string | undefined) => string | null },
    value?: number | string
) => meta.getIntroByCode(value) || DEFAULT_VALUE;

function getButtonValueLabel(value?: number | string): string {
    const type = Number(value);
    if (type === BUTTON_TYPE.PRODUCT.code) return "商品";
    if (type === BUTTON_TYPE.PRODUCT_TYPE.code) return "砂价";
    if (type === BUTTON_TYPE.WHARF.code) return "运价";
    if (type === BUTTON_TYPE.CONTACT.code) return "联系方式";
    if (type === BUTTON_TYPE.INFORMATION.code) return "资讯";
    return "按钮值";
}

/** 广告页只读详情，保留 Vue 的基础字段和按钮类型分支。 */
export function AdPageDetailPage({
    id,
    loadDetail = loadAdPageDetail,
    loadPicture = loadAdPagePicture,
    loadButtonValue = loadAdPageButtonValue,
    resolveContent = resolveAdPageContent,
    permissions
}: AdPageDetailPageProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 保持 Vue v-auth 语义：编辑入口独立受广告页写权限控制，封禁态只禁用按钮。
    const canWrite = (permissions ?? storePermissions).includes("ROLE_SS_AD_LIST_W");
    const routeId = id ?? Number(searchParams.get("id"));
    const [detail, setDetail] = useState<AdPageManagementRow | null>(null);
    const [picturePath, setPicturePath] = useState<string>();
    const [buttonDisplayValue, setButtonDisplayValue] = useState<string>();
    const [resolvedContent, setResolvedContent] = useState<string>();
    const [message, setMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!Number.isFinite(routeId) || routeId <= 0) return;
        let disposed = false;
        void loadDetail(routeId)
            .then(value => {
                if (!disposed) setDetail(value);
            })
            .catch(() => {
                if (!disposed) setMessage("广告详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadDetail, routeId]);

    useEffect(() => {
        if (!detail) return;
        let disposed = false;
        const advert = detail.advert;
        setPicturePath(undefined);
        setButtonDisplayValue(advert.buttonValue);
        setResolvedContent(undefined);

        // Vue 详情页按广告类型分别请求图片文件或还原富文本内容。
        void (async () => {
            try {
                if (Number(advert.type) === 1 && advert.pictureId) {
                    const picture = await loadPicture(advert.pictureId);
                    if (!disposed) setPicturePath(picture.path);
                }
                if (Number(advert.type) === 2 && advert.content) {
                    const content = await resolveContent(advert.content);
                    if (!disposed) setResolvedContent(content);
                }
            } catch {
                if (!disposed) setMessage("广告媒体内容加载失败");
            }
        })();

        // 旧 Vue 页面仅对关联实体按钮发起回查，联系方式继续展示广告原始值。
        const relatedButtonTypes = [
            BUTTON_TYPE.PRODUCT.code,
            BUTTON_TYPE.PRODUCT_TYPE.code,
            BUTTON_TYPE.WHARF.code,
            BUTTON_TYPE.INFORMATION.code
        ];
        if (relatedButtonTypes.includes(Number(advert.buttonType)) && advert.buttonValue) {
            void loadButtonValue(advert.buttonType, advert.buttonValue)
                .then(value => {
                    if (!disposed && value) setButtonDisplayValue(value);
                })
                .catch(() => {
                    // 关联数据失败时保留原始 ID，确保详情页仍可阅读和返回。
                });
        }

        return () => {
            disposed = true;
        };
    }, [detail, loadButtonValue, loadPicture, resolveContent]);

    const advert = detail?.advert;
    const hasButton = Number(advert?.isNeedButton) === 1;
    const canEdit = Number(advert?.used) === 0;

    return (
        <section aria-label="ad-page-detail-page" className="flex h-full min-h-full flex-col">
            <Card>
                {message ? <Alert message={message} role="alert" type="error" /> : null}
                <ZhDetailHeader
                    leftInfos={[{ label: "广告编号", value: advert?.id ?? DEFAULT_VALUE }]}
                    rightInfos={[
                        { label: "创建时间", value: formatDate(advert?.createdTime) },
                        { label: "更新时间", value: formatDate(advert?.updatedTime) }
                    ]}
                    title="广告页详情"
                />
                <ZhGrid className="mt-6" cols={4} gap={[50, 20]}>
                    <ZhBaseItem label="分类" value={formatMeta(CLASSIFY, advert?.classify)} />
                    <ZhBaseItem label="广告类型" value={formatMeta(ADVERT_TYPE, advert?.type)} />
                    <ZhBaseItem label="广告编号" value={advert?.id ?? DEFAULT_VALUE} />
                    <ZhBaseItem label="创建人" value={detail?.user?.name || DEFAULT_VALUE} />
                    <ZhBaseItem label="标题" value={advert?.title || DEFAULT_VALUE} />
                    <ZhBaseItem label="是否使用" span={3} value={formatMeta(YES_OR_NO, advert?.used)} />
                    {hasButton ? (
                        <>
                            <ZhBaseItem label="按钮类型" span={4} value={formatMeta(BUTTON_TYPE, advert?.buttonType)} />
                            <ZhBaseItem
                                label={getButtonValueLabel(advert?.buttonType)}
                                value={buttonDisplayValue || DEFAULT_VALUE}
                            />
                            <ZhBaseItem label="按钮名称" span={3} value={advert?.buttonName || DEFAULT_VALUE} />
                        </>
                    ) : null}
                    {Number(advert?.type) === ADVERT_TYPE.IMAGE.code ? (
                        <ZhBaseItem label="缩略图" span={4}>
                            {picturePath ? <ZhImage alt="广告页缩略图" fit="cover" src={picturePath} /> : DEFAULT_VALUE}
                        </ZhBaseItem>
                    ) : null}
                    {Number(advert?.type) === ADVERT_TYPE.TEXT.code ? (
                        <ZhBaseItem label="内容" span={4}>
                            {resolvedContent ? (
                                <div
                                    aria-label="广告页内容"
                                    className="max-w-3xl [&_img]:max-w-full [&_video]:max-w-full"
                                    // 对齐旧 Vue WangEditor 详情的 400px 媒体承载区，避免正文媒体撑破详情布局。
                                    style={RICH_TEXT_CONTENT_STYLE}
                                    dangerouslySetInnerHTML={{ __html: resolvedContent }}
                                />
                            ) : (
                                DEFAULT_VALUE
                            )}
                        </ZhBaseItem>
                    ) : null}
                </ZhGrid>
                {detail ? (
                    <Space className="mt-4">
                        {canEdit && canWrite ? (
                            <Button
                                aria-label={`编辑广告 ${routeId}`}
                                type="primary"
                                disabled={banButton}
                                // Vue 使用详情路由 query.id，避免接口回显编号变化时编辑错误广告。
                                onClick={() => void navigate(`/userOperation/adPageManage/edit?id=${routeId}`)}
                            >
                                编辑
                            </Button>
                        ) : null}
                        <Button aria-label="返回" disabled={banButton} onClick={() => void navigate(-1)}>
                            返回
                        </Button>
                    </Space>
                ) : null}
            </Card>
        </section>
    );
}

export default AdPageDetailPage;
